import { CaretDown, CheckSquare, Square } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/utils/utils";
import { Separator } from "@radix-ui/react-dropdown-menu";
import { Fragment } from "react/jsx-runtime";
import { cva, type VariantProps } from "class-variance-authority";

const selectDepartmentVariants = cva(
  "flex h-10 w-full items-center justify-between rounded-sm px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
  {
    variants: {
      variant: {
        dark: "bg-white/5 border border-input",
        default: "bg-[#1C1C1C]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);
export type DepartmentOption = {
  name: string;
  id: number | null;
};

type Props = {
  options: DepartmentOption[];
  value: DepartmentOption[];
  setValue: (
    department:
      | DepartmentOption[]
      | ((prev: DepartmentOption[]) => DepartmentOption[]),
  ) => void;
  variant?: VariantProps<typeof selectDepartmentVariants>["variant"];
};

export const entireCompany: DepartmentOption = {
  name: "Entire Company",
  id: null,
};

export const SelectDepartment = ({
  options,
  value,
  setValue,
  variant,
}: Props) => {
  const handleDepartmentChange = (e: Event, department: DepartmentOption) => {
    e.preventDefault();
    setValue((prev) => {
      if (prev.length === 1 && prev[0].id === null) {
        return department.id === null ? [] : [department];
      }
      if (department.id === null) return [entireCompany];
      const filtered = prev.filter((dep) => dep.id !== department.id);
      if (filtered.length === prev.length) {
        return [...prev, department];
      } else {
        return filtered;
      }
    });
  };
  const departments = [entireCompany, ...options];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(selectDepartmentVariants({ variant }))}
      >
        <span
          className={cn(
            "text-start text-sm capitalize",
            value.length === 0 && "text-muted-foreground",
          )}
        >
          {value.length === 0
            ? "Select departments"
            : value.map((dep) => dep.name).join(", ")}
        </span>
        <CaretDown className="ml-3 size-4 shrink-0 opacity-50" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-[50vh] w-[var(--radix-dropdown-menu-trigger-width)] overflow-auto px-3 py-3 scrollbar-thin">
        {departments.map((department, index) => {
          const { name, id } = department;
          const selected = value.some((dep) => dep.id === id);
          return (
            <Fragment key={`department-checkbox--fragment-${id}`}>
              {index !== 0 && (
                <Separator className="mx-3 h-[1px] bg-white/[0.1]" />
              )}
              <DropdownMenuItem
                key={`department-checkbox--${id}`}
                onSelect={(e) =>
                  handleDepartmentChange(e, department as DepartmentOption)
                }
                className="flex w-full items-center justify-between p-3 capitalize"
              >
                {name}
                {selected ? (
                  <CheckSquare weight="fill" className="size-6 shrink-0" />
                ) : (
                  <Square className="size-6 shrink-0 fill-white/30" />
                )}
              </DropdownMenuItem>
            </Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
